import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { getRequest, postRequest, deleteRequest, patchFileRequest } from "../helpers/fetchHandler"
import type { Activity, ActivitySubcategory, PaginationInfo } from "../constants/types"

const activityKeys = {
  all: ["activities"] as const,
  list: (params: { page: number; limit: number; search: string }) => ["activities", "list", params] as const,
  detail: (id: string | undefined) => ["activity", id] as const,
  categories: ["activityCategories"] as const
}

type ActivitiesResponse = {
  data: Activity[]
  pagination: PaginationInfo
}

type ListParams = {
  page?: number
  limit?: number
  search?: string
}

export function useActivities({ page = 1, limit = 50, search = "" }: ListParams) {
  return useQuery({
    queryKey: activityKeys.list({ page, limit, search }),
    queryFn: () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      })
      if (search) {
        params.append("search", search)
      }
      return getRequest({ url: `/api/v2/activities?${params}`, auth: true }) as Promise<ActivitiesResponse>
    }
  })
}

export function useActivity(id: string | undefined) {
  return useQuery({
    queryKey: activityKeys.detail(id),
    queryFn: () => getRequest({ url: `/api/v1/activities/activity/${id}`, auth: true }) as Promise<Activity>,
    enabled: !!id
  })
}

export function useActivityCategories() {
  return useQuery({
    queryKey: activityKeys.categories,
    queryFn: () =>
      getRequest({ url: "/api/v1/activities/subcategories", auth: true }) as Promise<ActivitySubcategory[]>,
    staleTime: 1000 * 60 * 30
  })
}

export function useCreateActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      postRequest({ url: "/api/v1/activities", data, auth: true }) as Promise<Activity>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.all })
    }
  })
}

export function useUpdateActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const formData = new FormData()
      Object.entries(data).forEach(([key, value]) => {
        if (value == null) {
          return
        } else if (key === "image") {
          formData.append(key, value as File, (value as File).name)
        } else if (key === "categories") {
          const categories = Array.isArray(value) ? value : [value]
          categories.forEach(categoryId => formData.append(`${key}[]`, categoryId as string))
        } else {
          formData.append(key, value as string)
        }
      })
      return patchFileRequest({ url: `/api/v1/activities/${id}`, data: formData, auth: true }) as Promise<Activity>
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: activityKeys.all })
      queryClient.invalidateQueries({ queryKey: activityKeys.detail(variables.id) })
    }
  })
}

export function useDeleteActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteRequest({ url: `/api/v1/activities/${id}`, auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.all })
    }
  })
}
